import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/*
 * Hairline buttons: 32px (28px small), 6px radius, 13px/500, sentence case.
 * default = primary in ink (one per view), outline = secondary, ghost = row actions,
 * destructive = danger ghost (Revoke, Delete triggers), danger = the filled
 * confirm action inside a destructive confirm dialog only.
 */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center gap-1.5 rounded-sm border border-transparent bg-clip-padding text-[13px] leading-none font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow] duration-[120ms] ease-out outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus active:not-aria-[haspopup]:translate-y-[0.5px] disabled:pointer-events-none disabled:opacity-55 aria-disabled:opacity-55 aria-invalid:border-danger [&_svg]:pointer-events-none [&_svg]:-mx-0.5 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-foreground text-background hover:bg-[color-mix(in_srgb,var(--fg)_84%,var(--bg))]",
        outline:
          "border-line-2 bg-background text-foreground shadow-xs hover:bg-bg-hover aria-expanded:bg-bg-hover",
        secondary:
          "border-line-2 bg-background text-foreground shadow-xs hover:bg-bg-hover aria-expanded:bg-bg-hover",
        ghost:
          "text-fg-2 hover:bg-bg-hover hover:text-foreground aria-expanded:bg-bg-hover aria-expanded:text-foreground",
        destructive: "text-danger hover:bg-danger-tint",
        danger:
          "bg-danger text-white shadow-xs hover:bg-[color-mix(in_srgb,var(--danger)_88%,black)] dark:text-[#0f0f11]",
        link: "h-auto! px-0! text-link hover:underline hover:underline-offset-[3px]",
      },
      size: {
        default: "h-8 px-3",
        xs: "h-7 px-2.5 [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-7 px-2.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 px-4",
        icon: "size-8 [&_svg]:mx-0",
        "icon-xs": "size-6 [&_svg]:mx-0 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-7 [&_svg]:mx-0 [&_svg:not([class*='size-'])]:size-4",
        "icon-lg": "size-9 [&_svg]:mx-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
